import type { BookkeepingType, BusinessNatureCategory, EntityType, ServiceModule } from "./types";

/**
 * Where a provisioned tenant lives — LAST RESORT ONLY.
 *
 * The real answer is per-environment (`TENANT_BASE_DOMAIN` on the server) and
 * now rides along on every client payload as `tenant_domain`; use
 * `coTenantDomain(client)` instead of this constant. Hardcoding it was wrong on
 * the dev deployment, which serves tenants under notion.zournalhq.com: every
 * screen quoted an initium address that go-live would then not use.
 */
export const CO_TENANT_DOMAIN_FALLBACK = "initium.zournalhq.com";

/** The tenant domain this environment actually serves. */
export function coTenantDomain(tenantDomain?: string): string {
  const value = (tenantDomain ?? "").trim();

  return value === "" ? CO_TENANT_DOMAIN_FALLBACK : value;
}

// Full Indian entity spread — same list the design repo's Chapter 1 company
// form offers.
export const ENTITY_TYPE_OPTIONS: { value: EntityType; label: string }[] = [
  { value: "private-limited", label: "Private Limited Company" },
  { value: "public-limited", label: "Public Limited Company" },
  { value: "llp", label: "Limited Liability Partnership (LLP)" },
  { value: "partnership", label: "Partnership" },
  { value: "proprietorship", label: "Proprietorship" },
  { value: "trust", label: "Trust" },
  { value: "section-8", label: "Section 8 Company" },
  { value: "huf", label: "Hindu Undivided Family (HUF)" },
  { value: "aop-boi", label: "Association of Persons / Body of Individuals (AOP/BOI)" },
];

export const BUSINESS_NATURE_OPTIONS: { value: BusinessNatureCategory; label: string }[] = [
  { value: "trading", label: "Trading" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "services", label: "Services" },
  { value: "retail", label: "Retail" },
  { value: "e-commerce", label: "E-commerce" },
  { value: "professional", label: "Professional" },
  { value: "other", label: "Other" },
];

export function businessNatureCategoryLabel(value: BusinessNatureCategory): string {
  const option = BUSINESS_NATURE_OPTIONS.find((candidate) => candidate.value === value);
  return option ? option.label : value;
}

export const SERVICE_MODULE_LABEL: Record<ServiceModule, string> = {
  books: "Books",
  compliance: "Compliance",
  cfo: "CFO",
};

/**
 * Single source for the bookkeeping-start wording: the option card on Books
 * questions and, later, the go-live certificate read from the same map, so the
 * two can't drift. The stored values never change — this is display text only,
 * which is why "existing-zoho-current" survives a label that no longer says
 * "up to date".
 */
export const BOOKKEEPING_TYPE_LABEL: Record<BookkeepingType, string> = {
  "existing-zoho-current": "Existing Zoho Books",
  "new-zoho-opening-balances": "New Zoho Books with opening balances",
  "new-company": "Completely new company",
  "catch-up": "Catch-up accounting",
};

/**
 * Same stored values as BOOKKEEPING_TYPE_LABEL. Only the words change, and
 * only on a Tally client — the go-live certificate and the catch-up step
 * still key off "existing-zoho-current".
 */
export const TALLY_BOOKKEEPING_TYPE_LABEL: Record<BookkeepingType, string> = {
  "existing-zoho-current": "Existing Tally company",
  "new-zoho-opening-balances": "Create a new company in Tally",
  "new-company": "Completely new company",
  "catch-up": "Catch-up accounting",
};

/**
 * What Confirm connection writes once the API has confirmed we are inside the
 * client's Zoho organisation.
 *
 * Stored rather than derived because the workspace preview needs it on every
 * screen, and the alternative was `doneSlugs.includes("confirm-connection")` —
 * which flips when someone presses Continue, not when the connection comes up.
 * A CA who watches it connect and then closes the tab had a preview still
 * reading "Not yet connected" on their next visit.
 */
export const ZOHO_CONNECTION_KEY = "zohoConnection";

/**
 * What Connect Tally writes once the open company has a name, then again once
 * the bank read has a count. The workspace preview uses it the same way it
 * uses zohoConnection: the company name and GSTIN also live on the client,
 * and this carries the bank count those columns do not.
 */
export const TALLY_CONNECTION_KEY = "tallyConnection";

export interface TallyConnectionSummary {
  companyName: string;
  gstin?: string | null;
  bankAccountCount?: number;
}

export interface ZohoConnectionSummary {
  organizationId: string | null;
  organizationName: string | null;
  /**
   * What the read-back found, so the workspace preview can say it without
   * re-reading. Optional because connections recorded before the read-back
   * existed have none of it, and a preview must render either way.
   */
  gstin?: string | null;
  gstinCount?: number;
  bankAccountCount?: number;
}
